import type { MediaType } from "@/generated/graphql";
import { MediaLinkSection } from "../MediaLinkSection";
import { getLinkableTypes, getMediaConfig } from "../config";

interface MediaLinksPanelProps {
  /** The media type we're linking FROM */
  sourceMediaType: MediaType;
  /** The ID of the source media (null for new uploads) */
  sourceMediaId: string | null;
  /** Currently selected link IDs per type */
  selectedLinks: Partial<Record<MediaType, string[]>>;
  /** Callback when links change */
  onLinksChange: (type: MediaType, ids: string[]) => void;
}

/**
 * MediaLinksPanel - Dynamic link sections based on media type config
 *
 * Renders MediaLinkSection for each linkable type defined in the config.
 * Used in both upload and edit modals.
 */
export function MediaLinksPanel({
  sourceMediaType,
  sourceMediaId,
  selectedLinks,
  onLinksChange,
}: MediaLinksPanelProps) {
  const linkableTypes = getLinkableTypes(sourceMediaType);

  if (linkableTypes.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-gray-700">Media Links</h3>
      <p className="text-xs text-gray-500">
        Link this {getMediaConfig(sourceMediaType).label.toLowerCase()} to related media
      </p>

      {linkableTypes.map((linkType) => (
        <MediaLinkSection
          key={linkType}
          mediaType={linkType}
          sourceMediaId={sourceMediaId}
          currentLinks={selectedLinks[linkType] || []}
          onLink={(ids: string[]) => onLinksChange(linkType, ids)}
        />
      ))}
    </div>
  );
}

export default MediaLinksPanel;
